/**
 * Conservative severity thresholds for the Services tab.
 * Aligns with CloudWatch alarm targets (CPU/memory 80%, p95 250ms) and API health
 * (degraded when recent 5xx sum implies ≥1 error/min).
 */

export type MetricSeverity = 'ok' | 'warn' | 'error' | 'neutral' | 'unknown';

export const SERVICES_CPU_PERCENT = { warnAt: 60, errorAt: 80 } as const;
export const SERVICES_MEMORY_PERCENT = { warnAt: 60, errorAt: 75 } as const;
export const SERVICES_P95_LATENCY_MS = { warnAt: 150, errorAt: 250 } as const;
/** Yellow for any positive rate below the API health degrade line (1.0/min). */
export const SERVICES_ERROR_RATE_5XX_PER_MIN = { errorAt: 1 } as const;

export const SERVICES_THRESHOLD_TOOLTIPS = {
  cpu:
    'Color thresholds (current and 24h high): green <60%, yellow 60–79%, red ≥80% (matches planned CPU alarm).',
  memory:
    'Color thresholds (current and 24h high): green <60%, yellow 60–74%, red ≥75% (conservative vs memory alarm).',
  p95:
    'Color thresholds (current and 24h high): green <150ms, yellow 150–249ms, red ≥250ms (matches planned p95 alarm).',
  error5xx:
    'Color thresholds (current and 24h peak): green 0/min, yellow >0 and <1/min, red ≥1/min (matches API degraded health).',
  tasks:
    'Color thresholds: green when running equals desired, yellow when partially running, red when desired >0 but none running, neutral when scaled to zero.',
} as const;

export function percentUtilizationSeverity(
  value: number | undefined,
  warnAt: number,
  errorAt: number,
): MetricSeverity {
  if (value === undefined || Number.isNaN(value)) {
    return 'unknown';
  }
  if (value >= errorAt) {
    return 'error';
  }
  if (value >= warnAt) {
    return 'warn';
  }
  return 'ok';
}

export function latencyMsSeverity(value: number | undefined): MetricSeverity {
  return percentUtilizationSeverity(
    value,
    SERVICES_P95_LATENCY_MS.warnAt,
    SERVICES_P95_LATENCY_MS.errorAt,
  );
}

export function errorRate5xxPerMinSeverity(value: number | undefined): MetricSeverity {
  if (value === undefined || Number.isNaN(value)) {
    return 'unknown';
  }
  if (value >= SERVICES_ERROR_RATE_5XX_PER_MIN.errorAt) {
    return 'error';
  }
  if (value > 0) {
    return 'warn';
  }
  return 'ok';
}

export function tasksSeverity(runningCount: number, desiredCount: number): MetricSeverity {
  if (desiredCount === 0) {
    return 'neutral';
  }
  if (runningCount === 0) {
    return 'error';
  }
  if (runningCount < desiredCount) {
    return 'warn';
  }
  return 'ok';
}

export function miniBarVariantForSeverity(
  severity: MetricSeverity,
): 'success' | 'default' | 'warning' | 'error' {
  switch (severity) {
    case 'error':
      return 'error';
    case 'warn':
      return 'warning';
    case 'ok':
      return 'success';
    default:
      return 'default';
  }
}

export function metricCellClassName(severity: MetricSeverity, muted = false): string {
  const base = muted ? 'td-muted' : '';
  switch (severity) {
    case 'ok':
      return [base, 'metric-cell-ok'].filter(Boolean).join(' ');
    case 'warn':
      return [base, 'metric-cell-warn'].filter(Boolean).join(' ');
    case 'error':
      return [base, 'metric-cell-error'].filter(Boolean).join(' ');
    case 'neutral':
      return [base, 'metric-cell-neutral'].filter(Boolean).join(' ');
    default:
      return base || 'td-muted';
  }
}
